<script lang="ts">
  // Mobile detection utility
  let isMobile = false
  if (typeof window !== "undefined") {
    isMobile =
      /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent,
      )
  }
  import { onMount } from "svelte"
  import { fade, fly } from "svelte/transition"
  import { browser } from "$app/environment"
  import DendriticBackground from "$lib/components/DendriticBackground.svelte"
  import DendriticControls from "$lib/components/dendritic/DendriticControls.svelte"
  import Seo from "$lib/components/Seo.svelte"
  import {
    organizationSchema,
    siteDescription,
    siteName,
    siteTitle,
    siteUrl,
  } from "$lib/seo"

  let loaded = false
  let currentSlide = 0
  let controlPanelOpen = false

  // UI state variables - The single source of truth
  let uiBloomStrength = 4.5
  let uiBloomRadius = 1.2
  let uiBloomThreshold = 0.2
  let uiActivationSpeed = 0.005
  let uiPropagationChance = 0.1
  let uiDecayRate = 0.06
  let uiPulseIntensity = 0.3
  let uiSpontaneousRate = 0.05
  let uiBranchGrowthRate = 0.001
  let uiBranchPruningRate = 0.0005
  let uiStrengthDecayRate = 0.001
  let uiEnableParticles = false
  let uiEnableDendrites = true
  let uiFogEnabled = true
  let uiFogColor = "#0a2540"
  let uiFogDensity = 0.018
  let enableLogging = false

  const aboutPanel = {
    title: "About the Dendritic Network",
    description: `
      <div style="font-size:0.93em; line-height:1.55;">
        <p>This is a real-time simulation of a <strong>spiking neural network</strong>, a model inspired by computational neuroscience. The spheres represent neurons, and the branching lines are their dendritic connections.</p>
        <p style="margin-top:0.8em;">The traveling pulses of light are "spikes"—discrete electrical signals that neurons use to transmit information. The complex waves of activity you see are not a pre-recorded animation; they are the <strong>emergent result</strong> of simple rules governing how each individual neuron integrates signals and decides when to fire.</p>
        <p style="margin-top:0.8em;">The network also exhibits <strong>neural plasticity</strong>. Over time, it dynamically grows new connections, strengthens frequently used pathways, and prunes underused ones. This process is analogous to learning and memory formation in a biological brain.</p>
        <p style="margin-top:0.8em;">You can interact with the simulation by clicking on neurons to trigger an activation pulse. Use the control panel to adjust the visual and behavioral parameters of the network in real-time.</p>
        <p style="margin-top:1em;"><strong>Technology:</strong> Three.js, WebGL, Svelte.</p>
        <div style="margin-top:1em;font-size:1em;text-align:center;"><a href="https://hollas.net" target="_blank" rel="noopener noreferrer" style="color:#38bdf8;text-decoration:none;">Hollas.net</a></div>
      </div>
    `,
  }

  const services = [
    {
      title: "Technology Vision & Leadership",
      description:
        "Expert guidance for digital transformation, technology strategy, and innovation. We have always worked in global markets—including Asia and the US. Align your business goals with modern technology, drive change, and empower your teams to deliver results in a fast-moving market.",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v2"/><path d="M12 20v2"/><path d="M4.93 4.93l1.41 1.41"/><path d="M17.66 17.66l1.41 1.41"/><circle cx="12" cy="12" r="5"/><path d="M12 7v5l3 3"/></svg>`,
      features: [
        "Strategic Vision",
        "Team Leadership & Mentoring",
        "Agile & Lean Methodologies",
        "Digital Transformation",
        "Stakeholder Engagement",
        "Change Management",
        "Cross-functional Team Building",
        "Executive Reporting & Communication",
      ],
    },
    {
      title: `Enterprise SaaS & Cloud\nArchitecture`,
      description:
        "Design, build, and scale secure, high-performance SaaS platforms on AWS, Azure, and GCP. We deliver robust cloud-native solutions, CI/CD automation, and microservices architectures for modern business needs. We have enormous experience with containerisation and DevOps, from the very beginning of the movement.",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" viewBox="0 0 20 20" fill="currentColor"><path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" /></svg>`,
      features: [
        "Java, Python, Rust, C#, Go",
        "Svelte, React, Angular",
        "Node.js, C, C++",
        "Kubernetes",
        "Cloud-Native Design",
        "CI/CD Automation",
        "Multi-Region Deployments",
        "Microservices & API Gateways",
      ],
    },
    {
      title: "AI, Data Analytics & LLMs",
      description:
        "AI and Machine Learning solutions for real business impact: LLM-powered chatbots, semantic search, vector embedding, predictive analytics, ETL, data analytics, and custom MLOps pipelines. Unlock new value from your data and automate complex processes.",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M8 8h8v8H8z"/><circle cx="12" cy="12" r="1.5"/></svg>`,
      features: [
        "Pytorch, Tensorflow, Langchain",
        "ETL & Data Analytics",
        "Vector Embedding & Search",
        "RAG (Retrieval Augmented Generation)",
        "MLOps & Data Pipelines",
        "GANs & Predictive Analytics",
        "Prompt Engineering",
        "Model Fine-tuning",
        "Semantic Search",
      ],
    },
    {
      title: "Cybersecurity & Compliance",
      description:
        "Comprehensive cybersecurity, risk, and compliance services. We help you achieve SOC2, ISO27001, and regulatory compliance, implement zero trust, automate security, and protect your business from evolving threats.",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/><circle cx="12" cy="16" r="2"/></svg>`,
      features: [
        "Cloud Security & IAM",
        "SOC2 & ISO27001",
        "CISSP Certified",
        "Risk Management",
        "Zero Trust Architecture",
        "Penetration Testing",
        "Security Automation",
        "Compliance Auditing",
      ],
    },
    {
      title: "Blockchain, Finance & Web3",
      description:
        "Blockchain isn't just finance—it's trust. We have deep experience building level 1 blockchains, as well as technology-first blockchain, DeFi, and Web3 solutions: smart contract engineering, tokenization, decentralized apps, NFT platforms, and secure digital asset infrastructure. We help you build, launch, and scale innovative products in the new financial frontier.",
      icon: `<svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a1 1 0 01.894.553l7 14A1 1 0 0117 18H3a1 1 0 01-.894-1.447l7-14A1 1 0 0110 2zm0 3.618L5.832 16h8.336L10 5.618z"/></svg>`,
      features: [
        "Smart Contract Development (Solidity, Rust)",
        "DeFi Protocols & DApps",
        "Tokenization & NFT Platforms",
        "Web3 Wallet Integration",
        "Decentralized Identity & Authentication",
        "Digital Asset Security & Custody",
        "Blockchain Analytics & Forensics",
        "DAO & Governance Systems",
      ],
    },
    {
      title: "Dare to be Different",
      description:
        "We sit at the forefront of technology—and always have. If something is better, we will adopt and follow it, not for the sake of it, but for results. We have a history of launching first-in-class products to success. Let us help you break new ground and stand out from the crowd.",
      icon: `<svg xmlns=\"http://www.w3.org/2000/svg\" class=\"h-12 w-12 text-pink-400\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M12 2v2\"/><path d=\"M12 20v2\"/><path d=\"M4.93 4.93l1.41 1.41\"/><path d=\"M17.66 17.66l1.41 1.41\"/><circle cx=\"12\" cy=\"12\" r=\"5\"/><path d=\"M12 7v5l3 3\"/></svg>`,
      features: [
        "First-in-class product launches",
        "Innovation leadership",
        "Market disruption",
        "Proven success stories",
        "Break new ground",
        "Stand out from the crowd",
      ],
    },
  ]

  function nextSlide() {
    currentSlide = (currentSlide + 1) % services.length
  }
  function prevSlide() {
    currentSlide = currentSlide === 0 ? services.length - 1 : currentSlide - 1
  }
  function goToSlide(index: number) {
    currentSlide = index
  }

  onMount(() => {
    if (!browser) return

    // Performance optimizations for animation
    // 1. Detect mobile or low-performance device and scale back animation
    function isLowPerformance() {
      // Basic mobile detection
      const ua = navigator.userAgent
      const isMobile =
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          ua,
        )
      // Try to detect low device memory or CPU (feature detection)
      const navAny = navigator as any
      const lowMemory =
        typeof navAny.deviceMemory === "number" && navAny.deviceMemory <= 2
      const lowHardwareConcurrency =
        typeof navAny.hardwareConcurrency === "number" &&
        navAny.hardwareConcurrency <= 2
      return isMobile || lowMemory || lowHardwareConcurrency
    }
    // 1a. Lower render resolution for low performance devices
    function getPixelRatio() {
      if (isLowPerformance()) return 1.2
      return window.devicePixelRatio || 1
    }
    ;(window as any).setPixelRatio = getPixelRatio // Expose for DendriticBackground usage

    // 2. Pause animation when tab is not visible (Page Visibility API)
    let animationPaused = false
    function handleVisibilityChange() {
      animationPaused = document.hidden
      // Optionally, you can further reduce animation parameters when hidden
    }
    document.addEventListener("visibilitychange", handleVisibilityChange)

    if (isLowPerformance()) {
      uiBloomStrength = 2.5
      uiBloomRadius = 0.7
      uiBloomThreshold = 0.3
      uiActivationSpeed = 0.002
      uiPropagationChance = 0.05
      uiDecayRate = 0.09
      uiPulseIntensity = 0.15
      uiSpontaneousRate = 0.02
      uiBranchGrowthRate = 0.0005
      uiBranchPruningRate = 0.0008
      uiStrengthDecayRate = 0.002
      uiEnableParticles = false
      uiEnableDendrites = true
      uiFogEnabled = true
      // Optionally, you could set a lower frame rate in your animation loop
      ;(window as any).__maxFrameRate = 30
      uiFogDensity = 0.025
      ;(window as any).__maxFrameRate = 60
    }

    loaded = true
    // 3. Throttle animation loop if tab is not visible
    const interval = setInterval(() => {
      if (!animationPaused) nextSlide()
    }, 12000)
    return () => {
      clearInterval(interval)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  })
</script>

<Seo
  title={siteTitle}
  description={siteDescription}
  schema={{
    "@graph": [
      organizationSchema,
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        url: siteUrl,
        name: siteName,
        description: siteDescription,
        publisher: { "@id": `${siteUrl}/#organization` },
      },
    ],
  }}
/>

{#if !isMobile}
  <div class="select-none fixed inset-0 z-0" style="pointer-events: none;">
    <DendriticBackground
      {uiBloomStrength}
      {uiBloomRadius}
      {uiBloomThreshold}
      {uiActivationSpeed}
      {uiPropagationChance}
      {uiDecayRate}
      {uiPulseIntensity}
      {uiSpontaneousRate}
      {uiBranchGrowthRate}
      {uiBranchPruningRate}
      {uiStrengthDecayRate}
      {uiEnableParticles}
      {uiEnableDendrites}
      {uiFogEnabled}
      {uiFogColor}
      {uiFogDensity}
      {enableLogging}
    />
  </div>
{/if}

<button
  class="control-toggle control-toggle-large"
  on:click={() => (controlPanelOpen = !controlPanelOpen)}
  aria-label={controlPanelOpen ? "Close controls" : "Open controls"}
  style="position:fixed;bottom:16px;right:16px;z-index:1000;pointer-events:auto;width:48px;height:48px;border-radius:50%;background:rgba(15,25,45,0.7);border:2px solid rgba(100,200,255,0.25);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 12px rgba(0,0,0,0.25);"
>
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    {#if controlPanelOpen}
      <path d="M6 15l6-6 6 6" />
    {:else}
      <path d="M6 9l6 6 6-6" />
    {/if}
  </svg>
</button>

{#if controlPanelOpen}
  <div
    style="position:fixed;bottom:70px;right:20px;z-index:1000;min-width:340px;min-height:320px;overflow:visible;"
  >
    <DendriticControls
      bind:controlPanelOpen
      bind:uiBloomStrength
      bind:uiBloomRadius
      bind:uiBloomThreshold
      bind:uiActivationSpeed
      bind:uiPropagationChance
      bind:uiDecayRate
      bind:uiPulseIntensity
      bind:uiSpontaneousRate
      bind:uiBranchGrowthRate
      bind:uiBranchPruningRate
      bind:uiStrengthDecayRate
      bind:uiEnableParticles
      bind:uiEnableDendrites
      bind:uiFogEnabled
      bind:uiFogColor
      bind:uiFogDensity
      bind:uiEnableLogging={enableLogging}
      {aboutPanel}
    />
  </div>
{/if}

<!-- Main content -->
<div
  class="relative isolate overflow-hidden min-h-screen flex items-center z-10"
  in:fade={{ duration: 500, delay: 200 }}
>
  <div class="w-full py-16 lg:py-24">
    <div class="mx-auto max-w-6xl px-6 lg:px-8">
      <div class="relative w-full flex items-center justify-center">
        <div
          class="hero-content-box bg-slate-900/95 backdrop-blur-xl rounded-2xl p-2 lg:p-4 border border-slate-700/50 min-h-[400px] sm:min-h-[500px] md:min-h-[580px] lg:min-h-[660px] w-full max-w-4xl mx-auto flex flex-col overflow-hidden z-10 shadow-2xl relative"
        >
          <div
            class="w-full text-center flex flex-col justify-between overflow-hidden min-h-0 flex-1"
          >
            <div class="pt-2 pb-1">
              <span
                class="inline-block text-sky-400 text-base lg:text-lg font-bold tracking-widest uppercase bg-slate-800/60 px-4 py-2 rounded-full shadow-lg mb-2"
              >
                Stockholm, Sweden - Consultancy Services
              </span>
            </div>
            <div
              in:fly={{ y: 50, duration: 600, delay: 200 }}
              class="flex flex-col justify-between overflow-visible min-h-0 flex-1"
            >
              <div class="relative w-full">
                <div class="relative w-full">
                  {#if services.length > 0 && services[currentSlide] && services[currentSlide].features}
                    {#key currentSlide}
                      <div
                        class="text-center flex flex-col items-center justify-center px-4 overflow-visible w-full"
                      >
                        <div class="flex flex-col items-center mb-2">
                          <span
                            class="text-4xl lg:text-5xl drop-shadow-lg mb-1 text-primary"
                            >{@html services[currentSlide].icon}</span
                          >
                        </div>
                        <h1
                          class="text-2xl lg:text-4xl xl:text-5xl font-extrabold tracking-tight text-white text-shadow mb-2 lg:mb-3 break-words whitespace-normal"
                        >
                          {#each services[currentSlide].title.split("\n") as line, i}
                            <span>{line}</span>
                            {#if i < services[currentSlide].title.split("\n").length - 1}<br
                              />{/if}
                          {/each}
                        </h1>
                        <div class="max-w-2xl mx-auto">
                          <p
                            class="text-lg lg:text-xl font-bold text-sky-200 mb-2"
                          >
                            {services[currentSlide].description}
                          </p>
                        </div>
                        <div
                          class="flex flex-wrap justify-center gap-2 mb-4 max-w-2xl mx-auto"
                        >
                          {#each services[currentSlide].features as feature}
                            <div
                              class="flex items-center bg-slate-800/70 rounded-full px-4 py-2 border border-sky-700/30 shadow-sm text-xs lg:text-sm font-semibold text-sky-200"
                            >
                              <span class="mr-2"
                                ><svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  class="h-4 w-4 text-green-400"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  ><path
                                    fill-rule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clip-rule="evenodd"
                                  /></svg
                                ></span
                              >{feature}
                            </div>
                          {/each}
                        </div>
                      </div>
                    {/key}
                  {:else}
                    <div class="text-center text-red-400 py-8">
                      No hero content available
                    </div>
                  {/if}
                </div>
              </div>
            </div>
            <div class="mt-4 flex-shrink-0">
              <div class="flex justify-center mb-2 space-x-1">
                {#each services as _, index}
                  <button
                    on:click={() => goToSlide(index)}
                    class="w-1.5 h-1.5 rounded-full transition-colors {currentSlide ===
                    index
                      ? 'bg-sky-400'
                      : 'bg-slate-500 hover:bg-slate-400'}"
                    aria-label="Go to service {index + 1}"
                  ></button>
                {/each}
              </div>
              <div class="flex items-center justify-center gap-x-2">
                <a
                  href="/services"
                  class="text-xs font-semibold leading-6 text-white hover:text-sky-400 transition-colors"
                  >View services <span aria-hidden="true">→</span></a
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- CTA Section - REPLACED WITH NEW TEXT -->
<div class="bg-slate-900/70 backdrop-blur-sm">
  <div class="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
    <div class="mx-auto max-w-2xl text-center">
      <h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">
        Navigate Complexity. Build for the Future.
      </h2>
      <p class="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-300">
        Whether you're developing a technology roadmap, architecting a resilient
        platform, or leveraging AI for a strategic advantage, the right
        expertise is crucial. Let's discuss how we can achieve your goals
        together.
      </p>
      <div class="mt-10 flex items-center justify-center gap-x-6">
        <a
          href="/contact"
          class="rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400"
        >
          Schedule a Consultation
        </a>
        <a href="/services" class="text-sm font-semibold leading-6 text-white"
          >Explore Our Services <span aria-hidden="true">→</span></a
        >
      </div>
    </div>
  </div>
</div>

<style>
  .text-shadow {
    text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.8);
  }
  .hero-content-box {
    position: relative;
    background: rgba(15, 25, 45, 0.4);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
    padding-bottom: 2.5em !important;
    min-height: 400px;
    display: flex;
    flex-direction: column;
    justify-content: stretch;
  }
  @media (min-width: 640px) {
    .hero-content-box {
      min-height: 500px;
    }
  }
  @media (min-width: 768px) {
    .hero-content-box {
      min-height: 580px;
    }
  }
  @media (min-width: 1024px) {
    .hero-content-box {
      min-height: 660px;
    }
  }
  :global(.backdrop-blur-md) {
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  :global(body) {
    position: relative;
    z-index: 1;
  }
</style>
